import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { AiTextArea } from "../../../../../../components/Form/AiTextArea";
import Input from "../../../../../../components/Form/Input/input";
import Label from "../../../../../../components/Form/Label";
import { Form, FormGroup } from "../../../../../../components/Form/styles";
import { Box } from "../../../../../../components/Layout";
import SaveBar from "../../../../../../components/SaveBar";
import { SubHeader } from "../../../../../../components/SubHeader";
import { HighlightItem } from "../../../../../../model/site";
import { clone, deepEqual } from "../../../../../../utils/objects";
import { sanitizeUrl } from "../../../../../../utils/url";
import { uuidv4 } from "../../../../../../utils/uuid";
import { ItemThumbnail } from "../../../../components/ItemThumbnail";
import { useItemPreview } from "../../../../hooks/Section";
import { navigationState } from "../../../Sections/navigationState";
import { HighlightsNavigationState } from "../../highlightsNavigationState";
import { Container, ThumbnailBox } from "./styles";

const ItemHighlightPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const highlightsNavigationState = navigationState.get() as HighlightsNavigationState;

  const headerTitle = useRef(
    highlightsNavigationState.editingHighlight?.id
      ? t("pages.editor.highlights.item.header.title.edit")
      : t("pages.editor.highlights.item.header.title.add")
  );

  const initialItemState = useRef<HighlightItem>(
    clone<HighlightItem>(
      highlightsNavigationState.editingHighlight || {
        id: uuidv4(),
        title: "",
        description: "",
        thumbnailType: "IMAGE",
        image: sanitizeUrl(""),
        emoji: "",
      }
    )
  );

  const [item, setItem] = useState(initialItemState.current);

  useItemPreview(highlightsNavigationState.currentSection, item, existing => existing.id === item.id);

  const isSaveDisabled = !item.title;

  const hasUnsavedChanges = () => !deepEqual(initialItemState.current, item);

  const saveHandler = () => {
    const items = highlightsNavigationState.currentSection.items || [];

    const itemIndex = items.findIndex(highlight => highlight.id === initialItemState.current.id);

    if (itemIndex >= 0) {
      items[itemIndex] = item;
    } else {
      items.push(item);
    }

    highlightsNavigationState.currentSection.items = items;
    navigate(-1);
  };

  return (
    <Container>
      <SubHeader
        title={headerTitle.current}
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.highlights.header.title"), to: "/editor/highlights" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Box>
        <ThumbnailBox>
          <ItemThumbnail
            types={["IMAGE", "EMOJI"]}
            thumbnailType={item.thumbnailType || "IMAGE"}
            onThumbnailChange={thumbnailType => setItem({ ...item, thumbnailType: thumbnailType as "IMAGE" | "EMOJI" })}
            image={item.image}
            emoji={item.emoji}
            onChangeEmoji={emoji => setItem({ ...item, emoji })}
            onChangeImage={image => setItem({ ...item, image })}
          />
        </ThumbnailBox>
        <Form>
          <FormGroup>
            <Input
              id="title"
              required
              type="text"
              value={item.title}
              autoCapitalize={"on"}
              maxLength={56}
              onChange={e => setItem({ ...item, title: e.target.value })}
              label={t("pages.editor.highlights.item.form.title")}
            />
          </FormGroup>
          <FormGroup>
            <AiTextArea
              id="description"
              required
              style={{ height: "8rem" }}
              maxLength={200}
              autoCapitalize="on"
              placeholder={t("pages.editor.highlights.item.form.description")}
              value={item.description || ""}
              onChange={description => setItem({ ...item, description })}
              aiField="highlights.description"
              aiContext={{ itemTitle: item.title }}
            />
            <Label
              htmlFor="description"
              maxLength={200}
              usedLength={item.description?.length || 0}
              required
              text={t("pages.editor.highlights.item.form.description")}
            />
          </FormGroup>
        </Form>
      </Box>
      <SaveBar onSave={saveHandler} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

export default ItemHighlightPage;
